/**
 * Symbol art for the Explore loading reels (display-only).
 *
 * Every symbol is drawn once per screen density into a small sprite canvas (plus a
 * pre-blurred "motion streak" version), and each reel's strip is built from those
 * sprites. The reels then only move with CSS transforms; nothing is redrawn per frame
 * and no CSS filter blur is used.
 *
 * The SlotCheck check is the pink-and-yellow check tile, an inline PNG data URL
 * (slotCheckTile.ts, the same pixels the approved mockup used): no network fetch, no
 * relative public path, and no SVG-in-canvas decode (the public SVG check icon only wraps this
 * PNG in an SVG <pattern>, which WebKit does not reliably draw into a canvas, and that
 * left the reels on the old plain gold check). It starts decoding as soon as
 * this module loads. Until it is decoded (or if it ever fails) the reels draw a vector
 * copy of the same tile, never the old gold check. The casino logo is loaded from
 * public/assets and falls back to a neutral "CASINO" plate; strips are redrawn when an
 * image arrives.
 */
import { ReelSymbol } from '../../slotReels';
import { SLOT_CHECK_TILE_PNG } from './slotCheckTile';

/** Reel geometry (CSS px). Mirrored in _m-explore-skeleton.scss. */
export const REEL_CELL_PX = 28;
export const REEL_WIDTH_PX = 34;
export const REEL_HEIGHT_PX = 54;
/** Symbol box inside a cell. */
export const REEL_SYMBOL_PX = 22;
/** Extra rows above / below the strip so the window never runs off its ends. */
export const REEL_STRIP_PAD_ROWS = 2;

const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif";
/** The SlotCheck check tile (inline PNG, 80 x 87). */
export const REEL_CHECK_TILE_SRC: string = SLOT_CHECK_TILE_PNG;
const CHECK_TILE_ASPECT = 80 / 87;

// ---------------------------------------------------------------------------
// Images (shared by every reel; loaded once per src)
// ---------------------------------------------------------------------------

type ImageEntry = { img: HTMLImageElement; ready: boolean; failed: boolean; waiters: Set<() => void> };
const imageCache = new Map<string, ImageEntry>();

/** Loads `src` once; `onReady` runs when it is drawable (never if it fails). */
export function loadReelImage(src: string, onReady: () => void): () => void {
  let entry = imageCache.get(src);
  if (!entry) {
    const img = new Image();
    const e: ImageEntry = { img, ready: false, failed: false, waiters: new Set() };
    const markReady = () => {
      if (e.ready) return;
      e.ready = true;
      e.waiters.forEach((w) => w());
      e.waiters.clear();
    };
    img.onload = () => {
      // Make sure the pixels are decoded before the first drawImage into a sprite.
      if (typeof img.decode === 'function') img.decode().then(markReady, markReady);
      else markReady();
    };
    img.onerror = () => {
      e.failed = true;
      e.waiters.clear();
    };
    img.decoding = 'async';
    img.src = src;
    imageCache.set(src, e);
    entry = e;
  }
  const current = entry;
  if (current.ready) return () => undefined;
  if (current.failed) return () => undefined;
  current.waiters.add(onReady);
  return () => current.waiters.delete(onReady);
}

// Start decoding the check tile as soon as the reels module loads, so it is ready
// long before the first loading card shows.
if (typeof Image !== 'undefined') loadReelImage(REEL_CHECK_TILE_SRC, () => undefined);

function readyImage(src: string | null): HTMLImageElement | null {
  if (!src) return null;
  const e = imageCache.get(src);
  return e && e.ready && !e.failed ? e.img : null;
}

export type ReelArtSources = {
  /** The current casino's logo (pickCasinoLogoSrc) or null. */
  logoSrc: string | null;
};

/** Images the reels want, in load order. */
export function reelImageSources(art: ReelArtSources): string[] {
  const list = [REEL_CHECK_TILE_SRC];
  if (art.logoSrc) list.push(art.logoSrc);
  return list;
}

/** Changes whenever a newly loaded image would change the drawn art. */
export function reelArtKey(art: ReelArtSources): string {
  return [
    readyImage(REEL_CHECK_TILE_SRC) ? 'c' : '-',
    art.logoSrc && readyImage(art.logoSrc) ? art.logoSrc : '-',
  ].join('|');
}

// ---------------------------------------------------------------------------
// Drawing
// ---------------------------------------------------------------------------

function roundRect(c: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const q = Math.min(r, w / 2, h / 2);
  c.beginPath();
  c.moveTo(x + q, y);
  c.lineTo(x + w - q, y);
  c.quadraticCurveTo(x + w, y, x + w, y + q);
  c.lineTo(x + w, y + h - q);
  c.quadraticCurveTo(x + w, y + h, x + w - q, y + h);
  c.lineTo(x + q, y + h);
  c.quadraticCurveTo(x, y + h, x, y + h - q);
  c.lineTo(x, y + q);
  c.quadraticCurveTo(x, y, x + q, y);
  c.closePath();
}

function drawImageFit(c: CanvasRenderingContext2D, img: HTMLImageElement, maxW: number, maxH: number, aspect?: number): void {
  const a = img.naturalWidth > 0 && img.naturalHeight > 0 ? img.naturalWidth / img.naturalHeight : aspect || 1;
  let w = maxW;
  let h = w / a;
  if (h > maxH) {
    h = maxH;
    w = h * a;
  }
  c.drawImage(img, -w / 2, -h / 2, w, h);
}

/**
 * Vector copy of the check tile (pink frame, yellow face, pink check), used only
 * until the PNG is decoded or if it fails. Never the old plain gold check.
 */
function drawCheckTileVector(c: CanvasRenderingContext2D, u: number): void {
  const h = u * 0.98;
  const w = h * CHECK_TILE_ASPECT;
  roundRect(c, -w / 2, -h / 2, w, h, w * 0.24);
  c.fillStyle = '#EC34A0';
  c.fill();
  const iw = w * 0.72;
  const ih = h * 0.72;
  roundRect(c, -iw / 2, -ih / 2, iw, ih, iw * 0.08);
  c.fillStyle = '#FFFB02';
  c.fill();
  c.beginPath();
  c.moveTo(-w * 0.22, h * 0.06);
  c.lineTo(-w * 0.06, h * 0.22);
  c.lineTo(w * 0.27, -h * 0.2);
  c.lineWidth = w * 0.1;
  c.strokeStyle = '#EC34A0';
  c.stroke();
}

/** Draws `sym` centred on (0, 0) in a box of size `u`. */
export function drawReelSymbol(c: CanvasRenderingContext2D, sym: ReelSymbol, u: number, art: ReelArtSources): void {
  c.save();
  c.lineJoin = 'round';
  c.lineCap = 'round';
  switch (sym) {
    case 'seven': {
      c.font = `900 ${u * 1.08}px ${FONT}`;
      c.textAlign = 'center';
      c.textBaseline = 'middle';
      const g = c.createLinearGradient(0, -u / 2, 0, u / 2);
      g.addColorStop(0, '#FF7A6E');
      g.addColorStop(1, '#C3161C');
      c.lineWidth = u * 0.16;
      c.strokeStyle = '#5A2E00';
      c.strokeText('7', 0, u * 0.04);
      c.lineWidth = u * 0.07;
      c.strokeStyle = '#FFD45E';
      c.strokeText('7', 0, u * 0.04);
      c.fillStyle = g;
      c.fillText('7', 0, u * 0.04);
      break;
    }
    case 'diamond': {
      const pts: [number, number][] = [
        [-0.26, -0.34],
        [0.26, -0.34],
        [0.45, -0.1],
        [0, 0.44],
        [-0.45, -0.1],
      ];
      c.beginPath();
      pts.forEach(([x, y], i) => (i ? c.lineTo(x * u, y * u) : c.moveTo(x * u, y * u)));
      c.closePath();
      const g = c.createLinearGradient(0, -u * 0.4, 0, u * 0.45);
      g.addColorStop(0, '#CFF8FF');
      g.addColorStop(0.5, '#5CC8FF');
      g.addColorStop(1, '#1F6FD6');
      c.fillStyle = g;
      c.fill();
      c.lineWidth = u * 0.04;
      c.strokeStyle = '#0B3D7A';
      c.stroke();
      c.strokeStyle = 'rgba(255,255,255,0.7)';
      c.lineWidth = u * 0.025;
      c.beginPath();
      c.moveTo(-0.45 * u, -0.1 * u);
      c.lineTo(0.45 * u, -0.1 * u);
      c.moveTo(-0.26 * u, -0.34 * u);
      c.lineTo(-0.12 * u, -0.1 * u);
      c.lineTo(0, 0.44 * u);
      c.moveTo(0.26 * u, -0.34 * u);
      c.lineTo(0.12 * u, -0.1 * u);
      c.lineTo(0, 0.44 * u);
      c.stroke();
      break;
    }
    case 'cherry': {
      c.strokeStyle = '#3BA84A';
      c.lineWidth = u * 0.07;
      c.beginPath();
      c.moveTo(-0.2 * u, 0.06 * u);
      c.quadraticCurveTo(-0.1 * u, -0.3 * u, 0.12 * u, -0.38 * u);
      c.moveTo(0.18 * u, 0.1 * u);
      c.quadraticCurveTo(0.12 * u, -0.2 * u, 0.12 * u, -0.38 * u);
      c.stroke();
      c.fillStyle = '#5CCB5F';
      c.beginPath();
      c.ellipse(0.26 * u, -0.36 * u, 0.13 * u, 0.06 * u, -0.4, 0, Math.PI * 2);
      c.fill();
      const berries: [number, number][] = [
        [-0.2, 0.2],
        [0.18, 0.25],
      ];
      berries.forEach(([cx, cy]) => {
        const g = c.createRadialGradient((cx - 0.06) * u, (cy - 0.07) * u, u * 0.02, cx * u, cy * u, u * 0.2);
        g.addColorStop(0, '#FF8A8A');
        g.addColorStop(0.6, '#E0202E');
        g.addColorStop(1, '#8A0014');
        c.fillStyle = g;
        c.beginPath();
        c.arc(cx * u, cy * u, u * 0.19, 0, Math.PI * 2);
        c.fill();
      });
      break;
    }
    case 'bell': {
      const g = c.createLinearGradient(-u * 0.4, 0, u * 0.4, 0);
      g.addColorStop(0, '#C98A12');
      g.addColorStop(0.45, '#FFE680');
      g.addColorStop(1, '#C98A12');
      c.fillStyle = g;
      c.strokeStyle = '#6B4300';
      c.lineWidth = u * 0.04;
      c.beginPath();
      c.moveTo(-0.38 * u, 0.24 * u);
      c.lineTo(0.38 * u, 0.24 * u);
      c.bezierCurveTo(0.26 * u, 0.12 * u, 0.3 * u, -0.34 * u, 0, -0.34 * u);
      c.bezierCurveTo(-0.3 * u, -0.34 * u, -0.26 * u, 0.12 * u, -0.38 * u, 0.24 * u);
      c.closePath();
      c.fill();
      c.stroke();
      c.beginPath();
      c.arc(0, -0.38 * u, 0.06 * u, 0, Math.PI * 2);
      c.fill();
      c.stroke();
      c.fillStyle = '#8A5A00';
      c.beginPath();
      c.arc(0, 0.32 * u, 0.08 * u, 0, Math.PI * 2);
      c.fill();
      break;
    }
    case 'bar': {
      roundRect(c, -0.46 * u, -0.2 * u, 0.92 * u, 0.4 * u, 0.06 * u);
      c.fillStyle = '#111318';
      c.fill();
      c.lineWidth = u * 0.05;
      c.strokeStyle = '#F2F2F2';
      c.stroke();
      c.font = `900 ${u * 0.3}px ${FONT}`;
      c.textAlign = 'center';
      c.textBaseline = 'middle';
      c.fillStyle = '#FFFFFF';
      c.fillText('BAR', 0, u * 0.015);
      break;
    }
    case 'star': {
      c.beginPath();
      for (let i = 0; i < 10; i += 1) {
        const a = -Math.PI / 2 + (i * Math.PI) / 5;
        const r = (i % 2 ? 0.19 : 0.46) * u;
        const x = Math.cos(a) * r;
        const y = Math.sin(a) * r + 0.03 * u;
        if (i) c.lineTo(x, y);
        else c.moveTo(x, y);
      }
      c.closePath();
      const g = c.createLinearGradient(0, -u * 0.45, 0, u * 0.4);
      g.addColorStop(0, '#FFF2A0');
      g.addColorStop(1, '#F59E0B');
      c.fillStyle = g;
      c.fill();
      c.lineWidth = u * 0.04;
      c.strokeStyle = '#8A5200';
      c.stroke();
      break;
    }
    case 'lemon': {
      c.rotate(-0.4);
      const g = c.createRadialGradient(-0.1 * u, -0.08 * u, u * 0.03, 0, 0, u * 0.42);
      g.addColorStop(0, '#FFF9B0');
      g.addColorStop(0.6, '#FFE03A');
      g.addColorStop(1, '#C99700');
      c.fillStyle = g;
      c.beginPath();
      c.ellipse(0, 0, 0.38 * u, 0.26 * u, 0, 0, Math.PI * 2);
      c.fill();
      c.beginPath();
      c.ellipse(0.4 * u, 0, 0.07 * u, 0.06 * u, 0, 0, Math.PI * 2);
      c.ellipse(-0.4 * u, 0, 0.07 * u, 0.06 * u, 0, 0, Math.PI * 2);
      c.fill();
      c.strokeStyle = '#9C7400';
      c.lineWidth = u * 0.03;
      c.beginPath();
      c.ellipse(0, 0, 0.38 * u, 0.26 * u, 0, 0, Math.PI * 2);
      c.stroke();
      break;
    }
    case 'jackpot': {
      roundRect(c, -0.48 * u, -0.3 * u, 0.96 * u, 0.6 * u, 0.1 * u);
      const g = c.createLinearGradient(0, -0.3 * u, 0, 0.3 * u);
      g.addColorStop(0, '#FF4D4F');
      g.addColorStop(1, '#8E0E12');
      c.fillStyle = g;
      c.fill();
      c.lineWidth = u * 0.05;
      c.strokeStyle = '#FFD45E';
      c.stroke();
      c.font = `900 ${u * 0.22}px ${FONT}`;
      c.textAlign = 'center';
      c.textBaseline = 'middle';
      c.fillStyle = '#FFE27A';
      c.fillText('JACK', 0, -0.1 * u);
      c.fillText('POT', 0, 0.14 * u);
      break;
    }
    case 'casino': {
      const logo = readyImage(art.logoSrc);
      if (logo) {
        drawImageFit(c, logo, u * 1.3, u * 0.86, 2);
      } else {
        roundRect(c, -0.62 * u, -0.3 * u, 1.24 * u, 0.6 * u, 0.12 * u);
        c.fillStyle = '#1B2433';
        c.fill();
        c.lineWidth = u * 0.04;
        c.strokeStyle = '#8A94A6';
        c.stroke();
        c.font = `800 ${u * 0.2}px ${FONT}`;
        c.textAlign = 'center';
        c.textBaseline = 'middle';
        c.fillStyle = '#E8ECF2';
        c.fillText('CASINO', 0, u * 0.01);
      }
      break;
    }
    case 'check':
    default: {
      const tile = readyImage(REEL_CHECK_TILE_SRC);
      if (tile) {
        drawImageFit(c, tile, u * 0.98 * CHECK_TILE_ASPECT, u * 0.98, CHECK_TILE_ASPECT);
      } else {
        drawCheckTileVector(c, u);
      }
      break;
    }
  }
  c.restore();
}

// ---------------------------------------------------------------------------
// Sprites and strips
// ---------------------------------------------------------------------------

/** Device-pixel scale for the sharp art (capped: these are small). */
export function reelArtScale(): number {
  const dpr = typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 1;
  return Math.max(1, Math.min(2, dpr));
}

function makeCanvas(w: number, h: number): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(w));
  c.height = Math.max(1, Math.round(h));
  return c;
}

function ctx2d(c: HTMLCanvasElement | null): CanvasRenderingContext2D | null {
  if (!c) return null;
  try {
    return c.getContext('2d');
  } catch {
    return null; // jsdom without canvas support
  }
}

const spriteCache = new Map<string, HTMLCanvasElement | null>();

/** One symbol, sharp, `REEL_WIDTH_PX` x `REEL_CELL_PX` at `scale`. */
function symbolSprite(sym: ReelSymbol, scale: number, art: ReelArtSources): HTMLCanvasElement | null {
  const key = `${sym}|${scale}|${reelArtKey(art)}`;
  if (spriteCache.has(key)) return spriteCache.get(key) as HTMLCanvasElement | null;
  const canvas = makeCanvas(REEL_WIDTH_PX * scale, REEL_CELL_PX * scale);
  const c = ctx2d(canvas);
  if (!c || !canvas) {
    spriteCache.set(key, null);
    return null;
  }
  c.translate(canvas.width / 2, canvas.height / 2);
  drawReelSymbol(c, sym, REEL_SYMBOL_PX * scale, art);
  spriteCache.set(key, canvas);
  return canvas;
}

/** Index of the strip symbol shown on canvas row `row` (rows run top to bottom). */
export function stripRowSymbolIndex(row: number, length: number): number {
  const k = length + REEL_STRIP_PAD_ROWS - row;
  return ((k % length) + length) % length;
}

/** Rows on the strip canvas (the strip plus padding both ends). */
export function stripRowCount(length: number): number {
  return length + 2 * REEL_STRIP_PAD_ROWS + 1;
}

/**
 * Strip transform (CSS px) for reel position `pos` (in symbols; the symbol with index
 * round(pos) sits on the payline). Symbols travel downwards as `pos` grows.
 */
export function stripOffsetPx(pos: number, length: number): number {
  const p = ((pos % length) + length) % length;
  return REEL_HEIGHT_PX / 2 + (p - length - REEL_STRIP_PAD_ROWS - 0.5) * REEL_CELL_PX;
}

/**
 * Draws a reel strip onto `canvas` (sized here). `blurred`: a vertical motion streak
 * (several faint offset copies) drawn at 1x, used only while the reel spins fast.
 */
export function drawReelStrip(
  canvas: HTMLCanvasElement,
  strip: ReelSymbol[],
  art: ReelArtSources,
  blurred: boolean
): void {
  const scale = blurred ? 1 : reelArtScale();
  const rows = stripRowCount(strip.length);
  const w = Math.round(REEL_WIDTH_PX * scale);
  const h = Math.round(rows * REEL_CELL_PX * scale);
  if (canvas.width !== w) canvas.width = w;
  if (canvas.height !== h) canvas.height = h;
  const c = ctx2d(canvas);
  if (!c) return;
  c.setTransform(1, 0, 0, 1, 0, 0);
  c.clearRect(0, 0, w, h);
  const cell = REEL_CELL_PX * scale;
  for (let row = 0; row < rows; row += 1) {
    const sprite = symbolSprite(strip[stripRowSymbolIndex(row, strip.length)], scale, art);
    if (!sprite) continue;
    const y = row * cell;
    if (!blurred) {
      c.globalAlpha = 1;
      c.drawImage(sprite, 0, y, w, cell);
      continue;
    }
    const copies = 7;
    c.globalAlpha = 0.2;
    for (let i = 0; i < copies; i += 1) {
      c.drawImage(sprite, 0, y + (i - (copies - 1) / 2) * cell * 0.16, w, cell);
    }
  }
  c.globalAlpha = 1;
}

/** One sharp symbol (reduced-motion reels). */
export function drawReelCell(canvas: HTMLCanvasElement, sym: ReelSymbol, art: ReelArtSources): void {
  const scale = reelArtScale();
  const w = Math.round(REEL_WIDTH_PX * scale);
  const h = Math.round(REEL_CELL_PX * scale);
  if (canvas.width !== w) canvas.width = w;
  if (canvas.height !== h) canvas.height = h;
  const c = ctx2d(canvas);
  if (!c) return;
  c.setTransform(1, 0, 0, 1, 0, 0);
  c.clearRect(0, 0, w, h);
  const sprite = symbolSprite(sym, scale, art);
  if (sprite) c.drawImage(sprite, 0, 0, w, h);
}

/** Drop cached sprites (e.g. after the casino changes). */
export function clearReelSprites(): void {
  spriteCache.clear();
}
